import { SdkError, AuthorizationError } from '@auth0/nextjs-auth0/errors'
import { OnCallbackContext } from '@auth0/nextjs-auth0/types'
import { NextRequest, NextResponse } from 'next/server'
import logger from '@reward-platform/logger/edge'
import {
  AUTH_COOKIES,
  AUTH_SESSION_COOKIE_PREFIXES,
  AUTH_SESSION_COOKIE_PREFIXES_TO_CLEAR_ON_CALLBACK,
  AUTH_TRANSACTION_COOKIE_PREFIX,
  SILENT_LOGIN_FLAG_EXPIRY_TIME_IN_SECONDS,
} from '../../utils'

export interface CustomCallbackParams {
  error: SdkError | null
  context: OnCallbackContext
  appBaseUrl: string
  errorRedirectPath?: string
}

const setSilentAuthCookie = (response: NextResponse) => {
  response.cookies.set(AUTH_COOKIES.HAS_RUN_SILENT_LOGIN_STATE, String(true), {
    path: '/',
    maxAge: SILENT_LOGIN_FLAG_EXPIRY_TIME_IN_SECONDS,
  })
  response.cookies.set(AUTH_COOKIES.HAS_SILENT_LOGIN_IN_PROGRESS_STATE, String(false), {
    path: '/',
    maxAge: 0,
  })
}

export const clearSilentLoginCookies = (response: NextResponse) => {
  response.cookies.set(AUTH_COOKIES.HAS_RUN_SILENT_LOGIN_STATE, String(false), {
    path: '/',
    maxAge: 0,
  })
  response.cookies.set(AUTH_COOKIES.HAS_SILENT_LOGIN_IN_PROGRESS_STATE, String(false), {
    path: '/',
    maxAge: 0,
  })
  return response
}

export const clearAuthSessionCookies = (response: NextResponse) => {
  const responseCookieNames = response.cookies.getAll().map((cookie) => cookie.name)
  responseCookieNames.forEach((cookie) => {
    if (AUTH_SESSION_COOKIE_PREFIXES.some((prefix) => cookie.startsWith(prefix))) {
      response.cookies.delete(cookie)
    }
  })
  return response
}

// Clears the legacy appSession and any __txn_ cookies from the response after a
// successful silent login
export const clearAppSessionAndTxnCookies = (response: NextResponse) => {
  const responseCookieNames = response.cookies.getAll().map((cookie) => cookie.name)
  responseCookieNames.forEach((cookieName) => {
    if (
      AUTH_SESSION_COOKIE_PREFIXES_TO_CLEAR_ON_CALLBACK.some((prefix) =>
        cookieName.startsWith(prefix)
      )
    ) {
      response.cookies.delete(cookieName)
    }
  })
  return response
}

// Only returns a URL inside the app. A leading slash is not enough: `//other.site` and
// `/\other.site` resolve to another site, so the resolved origin is compared instead
const getReturnToUrl = (returnTo: string | undefined, appBaseUrl: string) => {
  const appUrl = new URL(appBaseUrl)
  if (!returnTo?.startsWith('/')) {
    return appUrl
  }

  try {
    const returnToUrl = new URL(returnTo, appUrl)
    return returnToUrl.origin === appUrl.origin ? returnToUrl : appUrl
  } catch (e) {
    // `new URL` throws on a malformed host such as `//[`, which is not a place to return to
    return appUrl
  }
}

export const handleCallback = async ({
  error,
  context,
  appBaseUrl,
  errorRedirectPath = '/',
}: CustomCallbackParams) => {
  if (error && (error.cause as AuthorizationError)?.code !== 'login_required') {
    logger.error(error.message, { error })
    const errorResponse = NextResponse.redirect(
      new URL(
        `${errorRedirectPath}?authError=${(error.cause as AuthorizationError)?.code || error.code}`,
        appBaseUrl
      )
    )
    setSilentAuthCookie(errorResponse)
    return errorResponse
  }

  const returnToUrl = getReturnToUrl(context.returnTo, appBaseUrl)

  if (error && (error.cause as AuthorizationError)?.code === 'login_required') {
    const notAuthenticatedResponse = NextResponse.redirect(returnToUrl)
    // Set silent login cookie before redirecting
    clearAuthSessionCookies(notAuthenticatedResponse)
    setSilentAuthCookie(notAuthenticatedResponse)
    return notAuthenticatedResponse
  }

  const authenticatedResponse = NextResponse.redirect(returnToUrl)
  // Set silent login cookie before redirecting
  setSilentAuthCookie(authenticatedResponse)
  clearAppSessionAndTxnCookies(authenticatedResponse)
  return authenticatedResponse
}

export const clearTransactionCookies = (req: NextRequest, response: NextResponse) => {
  // This is a workaround for this issue: https://github.com/auth0/nextjs-auth0/issues/1917
  // The auth0 middleware sets some transaction cookies that are not deleted after the login flow completes.
  // This causes stale cookies to be used in subsequent requests and eventually causes the request header to be rejected because it is too large.
  const transactionCookiePath = process.env.NEXT_PUBLIC_BASE_PATH || '/'
  const reqCookieNames = req.cookies.getAll().map((cookie) => cookie.name)
  reqCookieNames.forEach((cookie) => {
    if (cookie.startsWith(AUTH_TRANSACTION_COOKIE_PREFIX)) {
      response.cookies.set(cookie, '', {
        path: transactionCookiePath,
        maxAge: 0,
      })
    }
  })
  return response
}
